import type { RecordingLease } from "@medimo/transcription-summary";
import type {
  BridgeTimer,
  EhrExtensionBridge,
  LateAcquireReply,
} from "./bridge";

/**
 * 録音リースを維持する間隔。拡張機能はこの 4 倍余りの 90 秒 heartbeat が途絶えると
 * 失効させる。裏のタブのタイマーは 1 分に 1 回まで間引かれることがあるが、
 * その間隔でも失効しない
 */
export const LEASE_HEARTBEAT_INTERVAL_MS = 20_000;

/**
 * 録音中でなくなってからリースを解放するまでの周期の数。停止の手順を通らずに録音が
 * 終わった場合に解放する。停止の手順の途中でマイクと文字起こしが止まりきる前に
 * 解放しないよう、2 周期続いたときに限る
 */
const IDLE_TICKS_BEFORE_RELEASE = 2;

export type RecordingLeaseHandle = RecordingLease & {
  /** 開始の手順の後始末。録音処理が引き継いでいなければ解放する */
  releaseIfUnclaimed: () => void;
};

export type AcquireRecordingLeaseResult =
  | { refused: true }
  | { refused: false; lease: RecordingLeaseHandle };

type Phase = "starting" | "recording" | "released";

const activeLeases = new Set<RecordingLeaseHandle>();

/**
 * 開始の準備中か録音中のリースがあるか。拡張機能に問われたときの答えに使い、
 * 拡張機能の応答を待たずに単独で始めた録音も拡張機能から見えるようにする
 */
export const hasActiveRecordingLease = () => activeLeases.size > 0;

/**
 * 録音リースを取得する。拒否するのは、拡張機能が録音中だと確かめられた返事を
 * 受け取ったときだけである。拡張機能が無い・返事が無い・読めない・途中で例外が
 * 起きた場合は、拡張機能に問わずに録音を始められるリースを返す。拡張機能を
 * 使っていない医師の録音を、このしくみの不具合で止めないためである。
 *
 * `onConflict` は、録音を始めた後で拡張機能も録音中だと分かったときに 1 回だけ呼ぶ。
 * どちらの録音も止めない。
 */
export const acquireRecordingLease = async (options: {
  bridge: EhrExtensionBridge | null;
  timer: BridgeTimer;
  onConflict: () => void;
  /** medimo Web が録音中か。停止の手順を通らずに録音が終わったことに気づくために使う */
  isRecording?: () => boolean;
  /** 医師が拡張機能の録音を確かめたうえで始める。拡張機能に問わない */
  skipExtension?: boolean;
}): Promise<AcquireRecordingLeaseResult> => {
  const { bridge, timer, onConflict } = options;
  const asksExtension = options.skipExtension !== true && bridge !== null;
  const state: {
    phase: Phase;
    leaseId: string | null;
    refused: boolean;
    warned: boolean;
    idleTicks: number;
    cancelTick: (() => void) | null;
  } = {
    phase: "starting",
    leaseId: null,
    refused: false,
    warned: false,
    idleTicks: 0,
    cancelTick: null,
  };

  const conflict = () => {
    if (state.warned) return;
    state.warned = true;
    try {
      onConflict();
    } catch {
      // 知らせられなくても録音は続ける
    }
  };

  const markRefused = () => {
    state.refused = true;
    if (state.phase === "recording") conflict();
  };

  const isRecording = () => {
    try {
      return options.isRecording?.() ?? true;
    } catch {
      return true;
    }
  };

  const releaseToExtension = (leaseId: string) => {
    try {
      bridge?.release(leaseId);
    } catch {
      // 解放を送れなくても、拡張機能は heartbeat の途絶で失効させる
    }
  };

  const release = () => {
    if (state.phase === "released") return;
    state.phase = "released";
    activeLeases.delete(handle);
    state.cancelTick?.();
    state.cancelTick = null;
    const leaseId = state.leaseId;
    state.leaseId = null;
    if (leaseId !== null) releaseToExtension(leaseId);
  };

  const adopt = (leaseId: string) => {
    if (state.phase === "released" || state.leaseId !== null) {
      // 解放した後か、すでに別のリースを持っているときに届いたリースは使わないため、
      // 失効を待たずに解放する
      releaseToExtension(leaseId);
      return;
    }
    state.leaseId = leaseId;
    // 拡張機能が録音を終えた後に取り直せたら、以後は拒否されたものとして扱わない
    state.refused = false;
  };

  const onLate = (reply: LateAcquireReply) => {
    try {
      if (reply.kind === "refused") {
        markRefused();
        return;
      }
      adopt(reply.leaseId);
    } catch {
      // 遅れた返事を扱えなくても録音は続ける
    }
  };

  const reacquire = async () => {
    if (bridge === null) return;
    state.leaseId = null;
    const outcome = await bridge.acquire(onLate);
    if (outcome.kind === "granted") adopt(outcome.leaseId);
    if (outcome.kind === "refused") markRefused();
  };

  /**
   * リースを維持する。拡張機能の更新でリースの記録が消えた場合などで失われていたら
   * 取り直し、返事が無くて取り直せなかったら次の周期でまた取り直す。取り直せない
   * ときも medimo Web の録音は止めない
   */
  const keepAlive = async () => {
    try {
      if (!asksExtension || bridge === null || state.phase === "released") {
        return;
      }
      if (state.leaseId === null) {
        if (bridge.present()) await reacquire();
        return;
      }
      if ((await bridge.heartbeat(state.leaseId)) === "lost") {
        await reacquire();
      }
    } catch {
      // 維持できなくても録音は続ける
    }
  };

  const tick = async () => {
    if (state.phase === "recording" && !isRecording()) {
      state.idleTicks += 1;
      if (state.idleTicks >= IDLE_TICKS_BEFORE_RELEASE) {
        release();
        return;
      }
    } else {
      state.idleTicks = 0;
    }
    await keepAlive();
  };

  const scheduleTick = () => {
    if (state.phase === "released") return;
    state.cancelTick = timer.schedule(() => {
      state.cancelTick = null;
      tick().then(scheduleTick, scheduleTick);
    }, LEASE_HEARTBEAT_INTERVAL_MS);
  };

  const handle: RecordingLeaseHandle = {
    confirm: async () => {
      // 返事を待たずに始めた場合は、ここで改めて問い合わせて開始を遅らせない
      if (state.leaseId !== null) await keepAlive();
    },
    claim: () => {
      if (state.phase !== "starting") return;
      state.phase = "recording";
      if (state.refused) conflict();
    },
    release,
    releaseIfUnclaimed: () => {
      if (state.phase === "starting") release();
    },
  };
  activeLeases.add(handle);
  scheduleTick();

  if (!asksExtension || bridge === null) {
    return { refused: false, lease: handle };
  }
  try {
    if (!bridge.present()) return { refused: false, lease: handle };
    const outcome = await bridge.acquire(onLate);
    if (outcome.kind === "refused") {
      release();
      return { refused: true };
    }
    if (outcome.kind === "granted") adopt(outcome.leaseId);
  } catch {
    // 問い合わせで例外が起きても、拡張機能に問わずに録音を始める
  }
  return { refused: false, lease: handle };
};
